import { useCallback, useEffect, useState } from 'react'

export interface UsePaginationHookProps {
  total?: number
  pageSize?: number
  initialPage?: number
}

export interface UsePaginationHookResult {
  /**
   * The current page number.
   */
  current: number
  /**
   * The total number of pages.
   */
  pages: number
  /**
   * The number of items per page.
   */
  size: number
  /**
   * Callback fired when the page is changed.
   *
   * @param {number} newPageNumber The page selected.
   */
  onPageNumberChange: (newPageNumber: number) => void
  /**
   * Callback fired when the number of items per page is changed.
   *
   * @param {number} newPageSize The new number of items per page.
   */
  onPageSizeChange: (newPageSize: number) => void
}

export default ({
  total = 0,
  pageSize = 25,
  initialPage = 1,
}: UsePaginationHookProps = {}): UsePaginationHookResult => {
  const [{ size, current }, setState] = useState<{ size: number; current: number }>({
    size: pageSize,
    current: initialPage,
  })

  useEffect(() => {
    if (Math.ceil(total / size) < current && current > 1) {
      setState((prevState) => ({ ...prevState, current: 1 }))
    }
  }, [total, current, size])

  const onPageNumberChange = useCallback((newPageNumber: number) => {
    setState((prevState) => ({ ...prevState, current: newPageNumber }))
  }, [])

  const onPageSizeChange = useCallback((newPageSize: number) => {
    setState((prevState) => ({ ...prevState, size: newPageSize, current: 1 }))
  }, [])

  return {
    current,
    pages: Math.ceil(total / size),
    size,
    onPageNumberChange,
    onPageSizeChange,
  }
}
